import React, { useMemo, useState } from 'react';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from 'react-router-dom';
import styled from 'styled-components';

import { TamaguiProvider } from '@utilities/tamagui';

import { AriaAlertContext } from './AriaAlertContext';
import { ErrorModal } from './commons/components/ErrorModal';
import { Footer } from './commons/components/Footer';
import { Nav } from './commons/components/Nav/Nav';
import { Page } from './commons/components/Page';
import { RedirectRouter } from './commons/components/RedirectRouter';
import { ScrollToTop } from './commons/components/ScrollToTop';
import { SignInModalControl } from './commons/components/SignInModalControl';
import { SignInModalVariant } from './commons/components/SignInModalVariant';
import { CARDSTAR_OVERRIDE_ACCOUNT_CREATION } from './commons/constants';
import { ifFeatureFlagOn } from './commons/utilities/feature-flags';
import { prefixRoute, prefixBaseRoute } from './commons/utilities/routes';
import { isIEUserAgent } from './commons/utilities/userAgent';
import { ErrorContext } from './ErrorContext';
import GlobalStyle from './GlobalStyle';
import { PageNotFound } from './pages/404';
import { Application } from './pages/Application';
import { Apply } from './pages/Apply';
import { UpdateAnnualIncome } from './pages/UpdateAnnualIncome';
import { SignInContext } from './SignInContext';

const HiddenAriaAlertBlock = styled.p`
  border: 0;
  clip-path: 'rect(0 0 0 0)';
  height: '1px';
  margin: '-1px';
  overflow: 'hidden';
  padding: 0;
  width: '1px';
  position: 'absolute';
  visibility: 'hidden';
  font-size: 0;
`;

const Wrapped404 = () => (
  <>
    <Nav />
    <Page>
      <PageNotFound />
      <Footer />
    </Page>
  </>
);

const App = () => {
  // state for showing modals across application
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [ariaAlertMessage, setAriaAlertMessage] = useState<string | null>(null);

  // user info state, for use when signing in
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [externalUserId, setExternalUserId] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [dateOfBirth, setDateOfBirth] = useState<string | null>(null);
  // Context provider values, prevents unnecessarily changing value props on every render
  const ariaAlertProviderValue = useMemo(
    () => ({ setAriaAlertMessage }),
    [setAriaAlertMessage]
  );
  const showErrorProviderValue = useMemo(
    () => ({ setShowErrorModal }),
    [setShowErrorModal]
  );
  const signInProviderValue = useMemo(
    () => ({
      setShowSignInModal,
      userEmail,
      setUserEmail,
      externalUserId,
      setExternalUserId,
      firstName,
      lastName,
      dateOfBirth,
      setFirstName,
      setLastName,
      setDateOfBirth,
    }),
    [
      setShowSignInModal,
      userEmail,
      setUserEmail,
      externalUserId,
      setExternalUserId,
      firstName,
      lastName,
      dateOfBirth,
      setFirstName,
      setLastName,
      setDateOfBirth,
    ]
  );

  const isCardStarRedirectOn = ifFeatureFlagOn(
    CARDSTAR_OVERRIDE_ACCOUNT_CREATION
  );
  const SignInModal = isCardStarRedirectOn
    ? SignInModalVariant
    : SignInModalControl;

  // PartnerCampaign is set at Apply page, this value will perist through the funnel via Provider
  if (isIEUserAgent()) {
    const { origin } = window.location;
    window.location.href = `${origin}/outdated-browser`;
    return null;
  }

  return (
    <>
      <GlobalStyle />
      <HiddenAriaAlertBlock
        role="alert"
        aria-live="assertive"
        aria-relevant="additions"
        aria-atomic="true"
      >
        {ariaAlertMessage}
      </HiddenAriaAlertBlock>
      <Router>
        <ScrollToTop />
        <TamaguiProvider>
          <AriaAlertContext.Provider value={ariaAlertProviderValue}>
            <ErrorContext.Provider value={showErrorProviderValue}>
              <SignInContext.Provider value={signInProviderValue}>
                <ErrorModal
                  showModal={showErrorModal}
                  setShowModal={setShowErrorModal}
                />
                <SignInModal
                  showModal={showSignInModal}
                  setShowModal={setShowSignInModal}
                />
                <Routes>
                  <Route path={prefixRoute('/')} element={<Apply />} />
                  <Route
                    path={prefixBaseRoute('/')}
                    element={<Navigate replace to={prefixRoute('/')} />}
                  />
                  {/* App path needs a trailing '*' since it has a separate <Routes /> within it */}
                  <Route
                    path={prefixRoute('/application/:applicationId/*')}
                    element={<Application />}
                  />
                  <Route
                    path={prefixBaseRoute(
                      '/annual-income/:incomeCollectionOutboundId'
                    )}
                    element={<UpdateAnnualIncome />}
                  />
                  <Route
                    path={prefixBaseRoute(
                      '/prosper-card-cardholder-agreement.pdf'
                    )}
                    element={<RedirectRouter />}
                  />
                  <Route path={prefixRoute('/*')} element={<Wrapped404 />} />
                  <Route
                    path={prefixBaseRoute('/*')}
                    element={<Wrapped404 />}
                  />
                  <Route
                    path="/"
                    element={<Navigate replace to={prefixRoute('/')} />}
                  />
                </Routes>
              </SignInContext.Provider>
            </ErrorContext.Provider>
          </AriaAlertContext.Provider>
        </TamaguiProvider>
      </Router>
    </>
  );
};

export default App;
